import { z } from 'zod/v4';

/**
 * An amfori Site ID: `<country>-<business partner>-<site>`, e.g.
 * `008-000012-001`. Its first two segments are the Business Partner ID's,
 * which ends in `-000` instead. Every stored id has this shape.
 */
export const AMFORI_SITE_ID_PATTERN = /^\d{3}-\d{6}-\d{3}$/;

/**
 * A full (trimmed) amfori Site ID. A `…-000` value is rejected: in amfori's
 * `NNN-NNNNNN-NNN` id scheme the `-000` suffix is the **Business Partner** id,
 * never a site — a common copy/paste mistake off the sync report that could
 * never match a synced site.
 */
export const AmforiSiteIdSchema = z
  .string()
  .trim()
  .min(1)
  .regex(AMFORI_SITE_ID_PATTERN, {
    message:
      'amforiSiteId must be a full amfori Site ID (e.g. 008-000012-001).',
  })
  .refine(v => !v.endsWith('-000'), {
    message:
      'amforiSiteId is a Business Partner ID (ends in -000); provide the amfori Site ID (e.g. 008-000012-001).',
  });

/**
 * What the check of a Site ID can warn about before it is linked to a
 * supplier (DELTA-2763). None blocks the link; each must be acknowledged.
 */
export const AMFORI_SITE_CHECK_WARNING_CODES = [
  'NEW_PARTNER',
  'COUNTRY_MISMATCH',
  'ADDRESS_DIFFERENT',
  'NAME_DIFFERENT',
  'NO_AUDITS',
  'BETTER_CANDIDATE',
] as const;

export const AmforiSiteCheckWarningCodeSchema = z.enum(
  AMFORI_SITE_CHECK_WARNING_CODES,
);

export type AmforiSiteCheckWarningCode = z.infer<
  typeof AmforiSiteCheckWarningCodeSchema
>;

/**
 * Body of adding a Site ID to a supplier, or of switching the supplier to the
 * Site ID's business partner. `acknowledgedWarnings` lists the check's warning
 * codes the person confirmed; the server re-runs the check and refuses the
 * write while any warning is left out.
 */
export const AmforiSupplierSiteInputSchema = z
  .object({
    amforiSiteId: AmforiSiteIdSchema,
    acknowledgedWarnings: z.array(AmforiSiteCheckWarningCodeSchema).optional(),
  })
  .strict();

export type AmforiSupplierSiteInput = z.infer<
  typeof AmforiSupplierSiteInputSchema
>;

/** Query of the check: the Site ID to check against the supplier. */
export const AmforiSupplierSiteCheckQuerySchema = z
  .object({ siteId: AmforiSiteIdSchema })
  .strict();

export type AmforiSupplierSiteCheckQuery = z.infer<
  typeof AmforiSupplierSiteCheckQuerySchema
>;
